import React from 'react';
import { Sentry } from '../sentry';
import i18n from '../i18n';
import Button from './shared/Button';
import Icon from './shared/Icon';

/**
 * ErrorBoundary - Catches JavaScript errors in child components
 * and displays a friendly fallback UI instead of a blank screen.
 *
 * Usage: Wrap your app or specific sections with <ErrorBoundary>
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render shows the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Log error details for debugging
    console.error('ErrorBoundary caught an error:', error);
    console.error('Component stack:', errorInfo.componentStack);

    this.setState({ errorInfo });

    // Report to Sentry with the React component stack as context.
    // No-op if Sentry was not initialized (no DSN configured).
    try {
      Sentry.withScope((scope) => {
        scope.setTag('source', 'react-error-boundary');
        if (errorInfo && errorInfo.componentStack) {
          scope.setContext('react', { componentStack: errorInfo.componentStack });
        }
        Sentry.captureException(error);
      });
    } catch (e) {
      // Never let error reporting itself crash the boundary
      console.error('Failed to report error to Sentry:', e);
    }
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  handleTryAgain = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      // Check if a custom fallback was provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div className="min-h-dvh bg-surface flex items-center justify-center p-4">
          <div className="max-w-md w-full text-center">
            {/* Error Icon */}
            <div className="mb-6">
              <div className="w-24 h-24 mx-auto bg-error-light rounded-full flex items-center justify-center">
                <Icon name="warning" className="w-12 h-12 text-error" />
              </div>
            </div>

            {/* Error Message */}
            <h1 className="font-display font-medium text-2xl text-content-primary mb-3">
              {i18n.t('errorBoundary.title')}
            </h1>
            <p className="text-content-secondary mb-8">
              {i18n.t('errorBoundary.description')}
            </p>

            {/* Action Buttons */}
            <div className="space-y-3">
              <Button onClick={this.handleTryAgain} fullWidth>
                {i18n.t('errorBoundary.tryAgainButton')}
              </Button>

              <Button variant="secondary" onClick={this.handleGoHome} fullWidth>
                {i18n.t('errorBoundary.goHomeButton')}
              </Button>

              <button
                onClick={this.handleReload}
                className="w-full text-content-secondary hover:text-content-primary py-2 transition-colors text-sm"
              >
                {i18n.t('errorBoundary.reloadButton')}
              </button>
            </div>

            {/* Debug Info (only in development) */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="mt-8 text-left">
                <summary className="cursor-pointer text-sm text-content-muted hover:text-content-secondary">
                  {i18n.t('errorBoundary.devDetailsSummary')}
                </summary>
                <div className="mt-2 p-4 bg-surface-tertiary rounded-lg overflow-auto max-h-48">
                  <p className="text-xs font-mono text-error whitespace-pre-wrap">
                    {this.state.error.toString()}
                  </p>
                  {this.state.errorInfo && (
                    <p className="text-xs font-mono text-content-secondary mt-2 whitespace-pre-wrap">
                      {this.state.errorInfo.componentStack}
                    </p>
                  )}
                </div>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
